import { useState } from "react";
import { withNamespaces } from "react-i18next";
import { Col, Row, Button, Spinner, Badge } from "reactstrap";
import paginationFactory, {
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import DateUtils from "src/services/utils/DateUtils";
import { 
    EMPTY_LIST, 
    INTERNATIONAL_DATE_FORMAT,
} from "src/common/constants"; 
import FilteringComponent from "./components/FilteringComponent";
import ComplianceMonitoringQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueriesService";
import { useHistory, useLocation } from "react-router-dom";
import { ComplianceQueryStatuses } from "src/modules/3rd-party-management/constants";

const Queries = ({ 
    t
}) => {
    const [ filters, setFilters ] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });
    const history = useHistory();
    const location = useLocation();

    const dateUtils = new DateUtils();

    const tableColumns = [
        {
            dataField: "id",
            text: "#",
            sort: true,
            style: {
                width: 120
            }
        },
        {
            dataField: "name",
            text: t("Title"),
            sort: true,
            style: {
                width: 180
            }
        },
        {
            dataField: "description",
            text: t('Description'),
            sort: true,
            style: {
                width: 180
            }
        },
        {
            dataField: "status",
            text: t('Status'),
            sort: true,
            style: {
                width: 100
            },
            formatter: (cell) => {
                const statusOption = ComplianceQueryStatuses.find((s) => s.value === cell);

                return (
                    <Badge
                        style={{
                            backgroundColor: statusOption.color || '#eee'
                        }}
                    >
                        {t(statusOption?.label || cell)}
                    </Badge>
                )
            }
        },
        {
            dataField: "createdAt",
            text: t("Date and Time"),
            sort: true,
            formatter: (cellContent) => {
                return (
                    dateUtils.convertDateToDate(
                        cellContent,
                        INTERNATIONAL_DATE_FORMAT
                    )
                )
            }
        },
        {
            dataField: "createdBy.fullName",
            text: t("Created By"),
            sort: false
        },
        {
            dataField: "itemsCount.enabledItemsCount",
            text: t("Used {{creditsLabel}}", {
                creditsLabel: 'Credits'
            }),
            sort: false
        },
        {
            dataField: "itemsCount.enabledItemsCount",
            text: t("Total Search Items"),
            sort: false
        },
        {
            dataField: "actions",
            text: t("Action"),
            sort: false,
            formatter: (_cell, { id, status, supplier_ids }) => {
                return (
                    <div className="actions">
                        <Button
                            color="primary"
                            outline
                            className="border-0"
                            onClick={() => {
                                if (status === 'finish' || status === 'in_progress') {
                                    history.push(`/admin/3rd-party/compliance/queries/${id}/details?type=monitoring`);
                                }
                                else {
                                    history.push(`/admin/3rd-party/compliance/queries/new?id=${id}&thirdparties=${supplier_ids.join(',')}&type=monitoring`, {
                                        from: `${location.pathname}${location.search}`
                                    });
                                }
                            }}
                        >
                            <i className="ri-eye-line"></i>
                        </Button>
                    </div>
                );
            }
        }
    ];

    const handleFetchQueries = useQuery({
        queryKey: [
            "3rd-party-admin-compliance-monitoring-queries",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringQueriesService.getInstance();

            return await service.fetchQueries(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load monitoring queries. Please try again.'), {
                type: "error"
            });
        }
    });

    const NoDataIndication = () =>
        handleFetchQueries.isFetched &&
        !handleFetchQueries.data?.items?.length ? (
            <div className="alert m-0" role="alert">
                <p
                style={{
                    textAlign: "center",
                    marginBottom: 0,
                }}
                >
                {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleTableChange = (type, { 
        page, 
        sizePerPage,
        sortField,
        sortOrder
    }) => {
        switch (type) {
            case "pagination":
                setFilters({
                    ...filters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;
            case 'sort':
                setFilters({
                    ...filters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    };

    return (
        <div>
            <Row>
                <Col sm="12">
                    <div className="d-flex justify-content-end mb-3">
                        <FilteringComponent 
                            onChange={(newFilters) => {
                                setFilters({
                                    ...filters,
                                    ...newFilters,
                                    pageIndex: 1
                                });
                            }}
                        />
                    </div>
                </Col>
            </Row>
            
            <Row>
                <Col sm="12">
                    <PaginationProvider
                        pagination={paginationFactory({
                            custom: true,
                            page: filters.pageIndex,
                            sizePerPage: filters.pageSize,
                            totalSize: handleFetchQueries.data?.itemsCount || 0,
                            withFirstAndLast: false,
                            alwaysShowAllBtns: true,
                            prePageText: (
                                <span>
                                    <i className="ri-arrow-left-s-line"></i> {t("Back")}
                                </span>
                            ),
                            nextPageText: (
                                <span>
                                    {t("Next")} <i className="ri-arrow-right-s-line"></i>
                                </span>
                            ),
                            prePageTitle: t("Pre page"),
                            firstPageTitle: t("Next page"),
                            showTotal:   false,
                            paginationTotalRenderer :   (from, to, size) => {
                                return (
                                    <span className="react-bootstrap-table-pagination-total">
                                        {`${t('Total Items')} ${size}`}
                                    </span>
                                )
                            },
                            hideSizePerPage: false,
                            sizePerPageList: [
                                {
                                    text: "25",
                                    value: 25,
                                },
                                {
                                    text: "50",
                                    value: 50,
                                }
                            ]
                        })}
                    >
                        {({ paginationProps, paginationTableProps }) => (
                            <>
                                <Row>
                                    <Col sm="12">
                                        <BootstrapTable
                                            remote={{
                                                pagination: true,
                                                filter: false,
                                                sort: true,
                                                cellEdit: false,
                                                search: false,
                                            }}
                                            loading={
                                                handleFetchQueries.isFetching ||
                                                handleFetchQueries.isLoading
                                            }
                                            overlay={overlayFactory({
                                                spinner: (
                                                    <Spinner
                                                        animation="border"
                                                        variant="primary"
                                                        size="md"
                                                    />
                                                ),
                                                text: `${t("Loading")}...`
                                            })}
                                            onTableChange={handleTableChange}
                                            defaultSorted={[]}
                                            keyField={"id"}
                                            responsive
                                            bordered={false}
                                            data={handleFetchQueries.data?.items || []}
                                            striped={false}
                                            columns={tableColumns}
                                            wrapperClasses="table-responsive"
                                            classes={"table tpdd-table"}
                                            headerWrapperClasses={"thead-light"}
                                            style={{
                                                overflowX: "auto",
                                            }}
                                            noDataIndication={() => <NoDataIndication />}
                                            {...paginationTableProps}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm="12">
                                        <div className="tpdd-pagination-style-1">
                                            <PaginationTotalStandalone
                                                { ...paginationProps }
                                            />

                                            <div>
                                                <PaginationListStandalone {...paginationProps} />

                                                <SizePerPageDropdownStandalone {...paginationProps} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </PaginationProvider>
                </Col>
            </Row>
        </div>
    );
}

export default withNamespaces()(
    Queries
);